import { bootcamps } from "@/content/bootcamps";
import { FR_INTRO_VIDEO_URLS } from "@/content/bootcamp-video-urls-fr";

/**
 * DB bootcamp slugs don't match the content-file slugs that hold the
 * HeyGen intro video URLs. This map bridges DB slug -> local content slug.
 * Add new entries here as new published DB bootcamps need a video.
 */
const DB_TO_LOCAL_SLUG: Record<string, string> = {
  "zero-investment-launch": "starting-business-zero-investment",
  "startup-confidence": "startup-confidence-launch-assurance",
  "growth-scaling-ops": "growing-scaling-your-business",
  "emerging-leaders": "leadership-excellence-emerging-leaders",
  // "Launch your MVP in 30 days" has no exact local twin; use the closest
  // growth-hacking presenter as a stand-in so the card has a video.
  "launch-mvp-in-30-days": "digital-product-growth-hacking",
  // getting-started-bootpreneurship and launch-101-free are free primers
  // with dedicated HeyGen intros — resolved via the EN/FR DB-slug maps
  // below rather than a local content twin.
};

const FR_URLS = FR_INTRO_VIDEO_URLS;

/**
 * Dedicated English intros for DB-only bootcamp slugs that have no
 * corresponding entry in `src/content/bootcamps.ts`. Keyed by DB slug.
 */
const EN_INTRO_VIDEO_URLS: Record<string, string> = {
  "getting-started-bootpreneurship":
    "https://files2.heygen.ai/aws_pacific/avatar_tmp/efa3e58d924f42098d5207af69ba21cf/b520ee1a1c474d2bbc5017e6ba92bf79.mp4?Expires=1781254424&Signature=SQeYTTZJEAlnMCoqwZ2Z7wU2ZWaFZyg0K86CSdEwf4vTHYGV6ZmipxuqqUfmvP9OB7APyUCTinQYQNAkg-MtgovLER9NAFvVagXf0Da1dUroPVFC3HmBZfWWcRdkKYojsNPdzJx7RRz~GfAlulP23NEhTBEHidGXjeJbAKeZpy6zkeMjMpp1km3Qb~Q7Y1NbNgO2t2Y9H-YZj3dRoCfwId06vDXL-C9ipJPc1ob8UkIU-yAfDqxcsOty0YhJpQi7Du~xQBPb1iNwhG2lOb5iilPx8l9vigc~fWkfhyX6a1zrgEqGf2Mls8qIeZhPAxYbV5TwJx8uEfmUxGy49jTTnw__&Key-Pair-Id=K38HBHX5LX3X2H",
  "launch-101-free":
    "https://files2.heygen.ai/aws_pacific/avatar_tmp/efa3e58d924f42098d5207af69ba21cf/1ea2fb8cd3324c859ed189151f945b86.mp4?Expires=1781254472&Signature=KnLd7z5XSBBsc3zz3F8hlUrFeu5sRKtmEXLG-G21X8b-bY4lRTDC8HSH1x0Cl-~NKqelHtzkMFUgMnIq83ujS2nngRQBCiGjKVgT2QURvNtt864uZIkebNOLIWEoDH81EDYRL6JllFWmf8WEWAfgvHVm5caa5KMrvOxx9fxF4jNNm~fr178-HI3MMUffFzce~Fpa~cOGvXUYzwWU3HQecm0o0zxucztgNJJp8IP0lJlX1K0RKGGjH~ZgoduaWMgjTjkmy~DyMERomxpJY3kkyOH-yA3xQptbSHmZOD~ttbnlSJa6h0pyCFVGm7ohf7DHe3D7DjzWOQGdzKmQe3kriQ__&Key-Pair-Id=K38HBHX5LX3X2H",
};

/**
 * Dedicated French intros for DB-only bootcamp slugs (same keying as
 * EN_INTRO_VIDEO_URLS). Consulted before falling back to the shared
 * FR_URLS map (which is keyed by local content slugs).
 */
const EXTRA_FR_INTRO_VIDEO_URLS: Record<string, string> = {
  "getting-started-bootpreneurship":
    "https://files2.heygen.ai/aws_pacific/avatar_tmp/efa3e58d924f42098d5207af69ba21cf/23aecc776f6a48bc8ed453c4b9c7aeef.mp4?Expires=1781254500&Signature=HvMeNiZp1wtttHRuQN3BDBIaBVN1OIA~peX9eqBtNtF0fffPXEa5BhUmMWmtZVN3dGoIttc0jOGdyJiF08GsiZDwzGTFUizHpn0HU~VKroY1yn2tDpKddsy0L0w5emmFvzQBoZSqcvdj51p3mpsR9RPtEe7O0evNZLVCvRtDcl3s3H33T31GDYAmOwhiNGlOLogPLTKz7n5kw0E5Ife-y8WX2smz~ffeERk4Oy2btoSmFLgni444SlqB6JOg3E4UMwr~y008c6tBH5aqkKwYaQTt8qx4xGi3IH6uUgNR4QJmyrkQZd8-Z48xXSIp8XjEW1Z5FhfNaPYS7OdBVKq2Ig__&Key-Pair-Id=K38HBHX5LX3X2H",
  "launch-101-free":
    "https://files2.heygen.ai/aws_pacific/avatar_tmp/efa3e58d924f42098d5207af69ba21cf/a9fe2cc82c4e43f9ba1ee7ca405d547e.mp4?Expires=1781254537&Signature=MU12MegdFuDaPJrL7qUxgfaIqbjyOkqii99TTdiYg2LosoE-qvEjsgUTOELuABj9iyvv2DFubWze9i2CcdMhyX~cMhxK8FsMs~tKy6ExHx5G3RMrCM8JcETB6-7V6L1Yjt5sepF7bF2tDKNz~PM7Fi1y39D3LV63M5PMD0I2HN8n14nksc7IxVmbPk1Duml3vnzEa8E-l~wCAvZkg7dXD107Xs-~6nMCLQnxSf1wZmMmcB7-4-8AiOqPxf5ha6aN7l3~sKG~wh6dmYWCoWw7TRSGtXRWxuXzXnYiWrqEEXf228yGOn~rF-8EPrC7L4pU~ayb1DJYj0ZihXBcxNHoiw__&Key-Pair-Id=K38HBHX5LX3X2H",
};

/**
 * Return the intro video URL for any bootcamp slug, whether it comes from
 * the DB (mapped via DB_TO_LOCAL_SLUG) or directly matches a local entry.
 *
 * When `lang` starts with "fr", a native French render is preferred. If no
 * FR render exists for that slug, we fall back to the English video so the
 * card still has a video instead of only the SVG cover.
 */
export function getIntroVideoForSlug(slug: string | null | undefined, lang?: string): string | undefined {
  if (!slug) return undefined;
  const localSlug = DB_TO_LOCAL_SLUG[slug] ?? slug;
  if (lang && lang.toLowerCase().startsWith("fr")) {
    const extraFr = EXTRA_FR_INTRO_VIDEO_URLS[slug];
    if (extraFr) return extraFr;
    const fr = FR_URLS[localSlug];
    if (fr) return fr;
  }
  const en = EN_INTRO_VIDEO_URLS[slug];
  if (en) return en;
  const bc = bootcamps.find((b) => b.slug === localSlug);
  return bc?.intro_video_url;
}